/**
 * Background health sync task.
 *
 * Uses expo-task-manager + expo-background-task to register a background task
 * that runs runHealthSync() even when the app is fully backgrounded.
 *
 * On iOS, expo-background-task uses BGTaskScheduler (BGAppRefreshTask /
 * BGProcessingTask), replacing the deprecated Background Fetch API.
 * On Android, it uses WorkManager.
 *
 * IMPORTANT: TaskManager.defineTask MUST be called at the top level
 * before AppRegistry.registerComponent (i.e. imported from index.ts).
 * It cannot be defined inside a React component or useEffect.
 */

import { Platform } from 'react-native';
import { runHealthSync } from './healthBackground';
import debug from './debug';

export const HEALTH_BACKGROUND_TASK = 'ELEPLAN_HEALTH_SYNC';

// Lazy-load both native modules so a missing native bridge (Expo Go, stale dev
// build, or web) is a silent no-op rather than a crash at import time.
let TaskManager: typeof import('expo-task-manager') | null = null;
let BackgroundTask: typeof import('expo-background-task') | null = null;

if (Platform.OS !== 'web') {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    TaskManager = require('expo-task-manager') as typeof import('expo-task-manager');
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    BackgroundTask = require('expo-background-task') as typeof import('expo-background-task');
  } catch {
    // Native modules not compiled in — background health sync disabled.
  }
}

// ── Task definition (must be top-level) ───────────────────────────────────────

if (TaskManager && BackgroundTask) {
  const _bt = BackgroundTask;
  try {
    TaskManager.defineTask(HEALTH_BACKGROUND_TASK, async () => {
      try {
        debug.log('ui', 'Background health sync task started');
        await runHealthSync(true);
        debug.log('ui', 'Background health sync task completed');
        return _bt.BackgroundTaskResult.Success;
      } catch (err) {
        debug.warn('Background health sync task failed', err);
        return _bt.BackgroundTaskResult.Failed;
      }
    });
  } catch {
    // defineTask failed — likely Expo Go without native modules.
  }
}

// ── Registration helpers ───────────────────────────────────────────────────────

/**
 * Register the background task. Safe to call multiple times — no-ops if
 * already registered. Call this once the user is authenticated.
 */
export async function registerHealthBackgroundTask(): Promise<void> {
  if (Platform.OS === 'web' || !BackgroundTask || !TaskManager) return;
  try {
    const status = await BackgroundTask.getStatusAsync();
    if (status === BackgroundTask.BackgroundTaskStatus.Restricted) {
      // Restricted means iOS has globally turned off background
      // refresh (Settings → General → Background App Refresh) or the
      // simulator doesn't support BGTaskScheduler at all (status: 1
      // appears on every cold-start in dev). The app correctly skips
      // registration here — there's nothing actionable, so log at info
      // rather than warn so it doesn't pollute the dev console on every
      // launch.
      debug.info('background-task', 'Health background task unavailable — skipping registration', { status });
      return;
    }

    const alreadyRegistered = await TaskManager.isTaskRegisteredAsync(HEALTH_BACKGROUND_TASK);
    if (alreadyRegistered) return;

    await BackgroundTask.registerTaskAsync(HEALTH_BACKGROUND_TASK, {
      minimumInterval: 15 * 60,
    });

    debug.log('ui', 'Health background task registered');
  } catch (err) {
    debug.warn('Failed to register health background task', err);
  }
}

/**
 * Unregister the background task. Call on sign-out.
 */
export async function unregisterHealthBackgroundTask(): Promise<void> {
  if (Platform.OS === 'web' || !BackgroundTask || !TaskManager) return;
  try {
    const registered = await TaskManager.isTaskRegisteredAsync(HEALTH_BACKGROUND_TASK);
    if (registered) {
      await BackgroundTask.unregisterTaskAsync(HEALTH_BACKGROUND_TASK);
      debug.log('ui', 'Health background task unregistered');
    }
  } catch (err) {
    debug.warn('Failed to unregister health background task', err);
  }
}
